import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const formatCurrency = (amount) =>
  `$${parseFloat(amount || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getStatusBadge = (status) => {
  const map = {
    paid: { color: 'bg-success/10 text-success', label: 'Paid' },
    partial: { color: 'bg-warning/10 text-warning', label: 'Partial' },
    overdue: { color: 'bg-error/10 text-error', label: 'Overdue' },
    sent: { color: 'bg-primary/10 text-primary', label: 'Sent' },
    draft: { color: 'bg-muted text-muted-foreground', label: 'Draft' },
    cancelled: { color: 'bg-muted text-muted-foreground', label: 'Cancelled' },
  };
  const s = (status || 'draft')?.toLowerCase();
  return map?.[s] || map?.draft;
};

const PaymentForm = ({ clientSecret, invoice, billingInfo, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    setErrorMsg('');

    try {
      const { error, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: { return_url: `${window.location?.origin}/customer-payment-portal` },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMsg(error?.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        const { error: confirmError } = await supabase?.functions?.invoke('confirm-payment', {
          body: { paymentIntentId: paymentIntent?.id },
        });
        if (confirmError) console.error('Confirm error:', confirmError);
        onSuccess(paymentIntent);
      }
    } catch (err) {
      setErrorMsg('An unexpected error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-muted/30 rounded-lg p-4 space-y-2">
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Invoice:</span>
          <span className="font-medium text-foreground">{invoice?.invoice_number}</span>
        </div>
        <div className="flex justify-between border-t border-border pt-2">
          <span className="font-semibold text-foreground">Amount Due:</span>
          <span className="font-bold text-lg text-primary">{formatCurrency(invoice?.amount_due)}</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-3">Payment Details</label>
        <div className="border border-input rounded-lg p-4 bg-background">
          <PaymentElement />
        </div>
      </div>

      {errorMsg && (
        <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" />
          <p className="text-sm text-error">{errorMsg}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="flex-1 px-4 py-3 border border-border rounded-lg text-foreground font-medium hover:bg-muted transition-smooth disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isProcessing ? (
            <><div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />Processing...</>
          ) : (
            <><Icon name="Lock" size={16} color="currentColor" />Pay {formatCurrency(invoice?.amount_due)}</>
          )}
        </button>
      </div>
    </form>
  );
};

const CustomerPaymentPortal = () => {
  const [searchInput, setSearchInput] = useState('');
  const [customerInfo, setCustomerInfo] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [activeTab, setActiveTab] = useState('invoices');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [billingInfo, setBillingInfo] = useState({
    firstName: '', lastName: '', email: '', address: '', city: '', state: '', postalCode: '', country: 'US'
  });
  const [billingStep, setBillingStep] = useState('billing'); // 'billing' | 'payment'
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [creatingIntent, setCreatingIntent] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchInput?.trim()) return;
    setLoading(true);
    setError('');
    setCustomerInfo(null);
    setInvoices([]);
    setPayments([]);
    setSelectedInvoice(null);

    try {
      const email = searchInput?.trim()?.toLowerCase();
      const { data: customer, error: custErr } = await supabase?.from('customers')?.select('id, name, email, phone, company, address')?.ilike('email', email)?.single();

      if (custErr || !customer) {
        setError('No account found with that email. Please contact your service representative.');
        setLoading(false);
        return;
      }

      setCustomerInfo(customer);
      setBillingInfo(prev => ({ ...prev, email: customer?.email, firstName: customer?.name?.split(' ')?.[0] || '', lastName: customer?.name?.split(' ')?.slice(1)?.join(' ') || '' }));

      const [invoicesRes, paymentsRes] = await Promise.all([
        supabase?.from('invoices')?.select('*, invoice_items(*), payments(*)')?.eq('customer_id', customer?.id)?.order('created_at', { ascending: false }),
        supabase?.from('payments')?.select('*')?.eq('customer_id', customer?.id)?.order('payment_date', { ascending: false }),
      ]);

      if (invoicesRes?.error) throw invoicesRes?.error;
      if (paymentsRes?.error) throw paymentsRes?.error;

      setInvoices(invoicesRes?.data || []);
      setPayments(paymentsRes?.data || []);
    } catch (err) {
      setError('Failed to load account information. Please try again.');
      console.error('Portal search error:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = (invoice) => {
    setPayingInvoice(invoice);
    setBillingStep('billing');
    setClientSecret('');
    setPaymentSuccess(false);
    setShowPaymentModal(true);
  };

  const handleProceedToPayment = async () => {
    if (!billingInfo?.firstName || !billingInfo?.lastName || !billingInfo?.email || !billingInfo?.address || !billingInfo?.city || !billingInfo?.postalCode) {
      alert('Please fill in all required billing fields.');
      return;
    }
    setCreatingIntent(true);
    try {
      const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
        body: {
          paymentData: {
            amount: payingInvoice?.amount_due,
            currency: 'usd',
            invoiceId: payingInvoice?.id,
          },
          customerInfo: {
            userId: null,
            firstName: billingInfo?.firstName,
            lastName: billingInfo?.lastName,
            email: billingInfo?.email,
            stripeCustomerId: null,
            billing: {
              address_line_1: billingInfo?.address,
              city: billingInfo?.city,
              state: billingInfo?.state,
              postal_code: billingInfo?.postalCode,
              country: billingInfo?.country,
            },
          },
        },
      });

      if (error || data?.error) throw new Error(data?.error || error?.message);
      setClientSecret(data?.clientSecret);
      setBillingStep('payment');
    } catch (err) {
      alert('Failed to initialize payment. Please try again.');
      console.error('Payment intent error:', err?.message);
    } finally {
      setCreatingIntent(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    setPaymentSuccess(true);
    // Refresh invoices and payments
    const [invoicesRes, paymentsRes] = await Promise.all([
      supabase?.from('invoices')?.select('*, invoice_items(*), payments(*)')?.eq('customer_id', customerInfo?.id)?.order('created_at', { ascending: false }),
      supabase?.from('payments')?.select('*')?.eq('customer_id', customerInfo?.id)?.order('payment_date', { ascending: false }),
    ]);
    if (!invoicesRes?.error) setInvoices(invoicesRes?.data || []);
    if (!paymentsRes?.error) setPayments(paymentsRes?.data || []);
  };

  const unpaidInvoices = invoices?.filter(inv => !['paid', 'cancelled']?.includes(inv?.invoice_status?.toLowerCase()));
  const totalOutstanding = unpaidInvoices?.reduce((sum, inv) => sum + parseFloat(inv?.amount_due || 0), 0);

  return (
    <>
      <Helmet><title>Customer Payment Portal | RIGJOCKEY</title></Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="CreditCard" size={22} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-xl font-heading font-bold text-foreground">Customer Payment Portal</h1>
              <p className="text-sm text-muted-foreground">View invoices and make secure online payments</p>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-8">
          {/* Search */}
          {!customerInfo && (
            <div className="max-w-lg mx-auto">
              <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={28} color="var(--color-primary)" />
                </div>
                <h2 className="text-xl font-heading font-semibold text-foreground mb-2">Find Your Account</h2>
                <p className="text-muted-foreground mb-6">Enter your email address to view your invoices and payment history.</p>
                <form onSubmit={handleSearch} className="space-y-4">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e?.target?.value)}
                    className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    required
                  />
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-left">
                      <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                      <p className="text-sm text-error">{error}</p>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? <><div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />Searching...</> : <><Icon name="Search" size={16} color="currentColor" />Find My Account</>}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Customer Dashboard */}
          {customerInfo && (
            <div className="space-y-6">
              {/* Customer Info Bar */}
              <div className="bg-card rounded-xl border border-border p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{customerInfo?.name}</p>
                    <p className="text-sm text-muted-foreground">{customerInfo?.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setCustomerInfo(null); setSearchInput(''); setInvoices([]); setPayments([]); }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-smooth flex items-center gap-1"
                >
                  <Icon name="LogOut" size={14} color="currentColor" />Sign Out
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-card rounded-xl border border-border p-4">
                  <p className="text-sm text-muted-foreground mb-1">Total Invoices</p>
                  <p className="text-2xl font-bold text-foreground">{invoices?.length}</p>
                </div>
                <div className="bg-card rounded-xl border border-border p-4">
                  <p className="text-sm text-muted-foreground mb-1">Outstanding Balance</p>
                  <p className="text-2xl font-bold text-error">{formatCurrency(totalOutstanding)}</p>
                </div>
                <div className="bg-card rounded-xl border border-border p-4">
                  <p className="text-sm text-muted-foreground mb-1">Total Paid</p>
                  <p className="text-2xl font-bold text-success">{formatCurrency(payments?.reduce((s, p) => s + parseFloat(p?.amount || 0), 0))}</p>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 bg-muted/50 p-1 rounded-lg w-fit">
                {['invoices', 'payments']?.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth capitalize ${activeTab === tab ? 'bg-card text-foreground shadow-elevation-1' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {tab === 'invoices' ? `Invoices (${invoices?.length})` : `Payment History (${payments?.length})`}
                  </button>
                ))}
              </div>

              {/* Invoices Tab */}
              {activeTab === 'invoices' && (
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  {invoices?.length === 0 ? (
                    <div className="p-12 text-center">
                      <Icon name="FileText" size={40} color="var(--color-muted-foreground)" />
                      <p className="text-muted-foreground mt-3">No invoices found</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50 border-b border-border">
                          <tr>
                            <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Invoice #</th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Date</th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Due Date</th>
                            <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Total</th>
                            <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Amount Due</th>
                            <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                            <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {invoices?.map(inv => {
                            const badge = getStatusBadge(inv?.invoice_status);
                            const canPay = !['paid', 'cancelled']?.includes(inv?.invoice_status?.toLowerCase()) && parseFloat(inv?.amount_due || 0) > 0;
                            return (
                              <tr key={inv?.id} className="hover:bg-muted/30 transition-smooth">
                                <td className="px-4 py-3">
                                  <button onClick={() => setSelectedInvoice(selectedInvoice?.id === inv?.id ? null : inv)} className="font-medium text-primary hover:underline">
                                    {inv?.invoice_number}
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(inv?.created_at)}</td>
                                <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(inv?.due_date)}</td>
                                <td className="px-4 py-3 text-right font-medium text-foreground">{formatCurrency(inv?.total_amount)}</td>
                                <td className="px-4 py-3 text-right font-bold text-foreground">{formatCurrency(inv?.amount_due)}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${badge?.color}`}>{badge?.label}</span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {canPay ? (
                                    <button
                                      onClick={() => handlePayNow(inv)}
                                      className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 transition-smooth flex items-center gap-1 mx-auto"
                                    >
                                      <Icon name="CreditCard" size={12} color="currentColor" />Pay Now
                                    </button>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Invoice Detail Expand */}
                  {selectedInvoice && (
                    <div className="border-t border-border p-6 bg-muted/20">
                      <h3 className="font-semibold text-foreground mb-4">Invoice {selectedInvoice?.invoice_number} — Line Items</h3>
                      {selectedInvoice?.invoice_items?.length > 0 ? (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-muted-foreground">
                              <th className="text-left pb-2">Description</th>
                              <th className="text-right pb-2">Qty</th>
                              <th className="text-right pb-2">Unit Price</th>
                              <th className="text-right pb-2">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {selectedInvoice?.invoice_items?.map(item => (
                              <tr key={item?.id}>
                                <td className="py-2 text-foreground">{item?.description}</td>
                                <td className="py-2 text-right text-muted-foreground">{item?.quantity}</td>
                                <td className="py-2 text-right text-muted-foreground">{formatCurrency(item?.unit_price)}</td>
                                <td className="py-2 text-right font-medium text-foreground">{formatCurrency(item?.line_total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : <p className="text-muted-foreground text-sm">No line items</p>}
                      <div className="mt-4 pt-4 border-t border-border space-y-1 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Subtotal:</span><span>{formatCurrency(selectedInvoice?.subtotal)}</span></div>
                        {parseFloat(selectedInvoice?.tax_amount || 0) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Tax ({selectedInvoice?.tax_rate}%):</span><span>{formatCurrency(selectedInvoice?.tax_amount)}</span></div>}
                        {parseFloat(selectedInvoice?.discount_amount || 0) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount:</span><span className="text-success">-{formatCurrency(selectedInvoice?.discount_amount)}</span></div>}
                        <div className="flex justify-between font-bold text-base border-t border-border pt-1"><span>Total:</span><span>{formatCurrency(selectedInvoice?.total_amount)}</span></div>
                        <div className="flex justify-between text-success"><span>Amount Paid:</span><span>{formatCurrency(selectedInvoice?.amount_paid)}</span></div>
                        <div className="flex justify-between font-bold text-error"><span>Balance Due:</span><span>{formatCurrency(selectedInvoice?.amount_due)}</span></div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Payments Tab */}
              {activeTab === 'payments' && (
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  {payments?.length === 0 ? (
                    <div className="p-12 text-center">
                      <Icon name="Receipt" size={40} color="var(--color-muted-foreground)" />
                      <p className="text-muted-foreground mt-3">No payment history found</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50 border-b border-border">
                          <tr>
                            <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Payment #</th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Date</th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Method</th>
                            <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Transaction ID</th>
                            <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {payments?.map(pmt => (
                            <tr key={pmt?.id} className="hover:bg-muted/30 transition-smooth">
                              <td className="px-4 py-3 font-medium text-foreground">{pmt?.payment_number || '—'}</td>
                              <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(pmt?.payment_date)}</td>
                              <td className="px-4 py-3 text-sm text-foreground">{pmt?.payment_method || '—'}</td>
                              <td className="px-4 py-3 text-sm text-muted-foreground font-mono">{pmt?.transaction_id ? pmt?.transaction_id?.substring(0, 20) + '...' : '—'}</td>
                              <td className="px-4 py-3 text-right font-bold text-success">{formatCurrency(pmt?.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Payment Modal */}
      {showPaymentModal && payingInvoice && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-lg">
            <div className="border-b border-border p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="CreditCard" size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <h2 className="text-xl font-heading font-semibold text-foreground">
                    {paymentSuccess ? 'Payment Successful!' : billingStep === 'billing' ? 'Billing Information' : 'Secure Payment'}
                  </h2>
                  <p className="text-sm text-muted-foreground">{payingInvoice?.invoice_number}</p>
                </div>
              </div>
              {!paymentSuccess && (
                <button onClick={() => setShowPaymentModal(false)} className="p-2 rounded-lg hover:bg-muted transition-smooth">
                  <Icon name="X" size={20} color="var(--color-foreground)" />
                </button>
              )}
            </div>

            <div className="p-6">
              {paymentSuccess ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="CheckCircle" size={32} color="var(--color-success)" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Payment Received!</h3>
                  <p className="text-muted-foreground mb-6">Your payment of {formatCurrency(payingInvoice?.amount_due)} has been processed successfully.</p>
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth"
                  >
                    Close
                  </button>
                </div>
              ) : billingStep === 'billing' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">First Name *</label>
                      <input value={billingInfo?.firstName} onChange={e => setBillingInfo(p => ({ ...p, firstName: e?.target?.value }))} className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Last Name *</label>
                      <input value={billingInfo?.lastName} onChange={e => setBillingInfo(p => ({ ...p, lastName: e?.target?.value }))} className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                    <input type="email" value={billingInfo?.email} onChange={e => setBillingInfo(p => ({ ...p, email: e?.target?.value }))} className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Address *</label>
                    <input value={billingInfo?.address} onChange={e => setBillingInfo(p => ({ ...p, address: e?.target?.value }))} className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">City *</label>
                      <input value={billingInfo?.city} onChange={e => setBillingInfo(p => ({ ...p, city: e?.target?.value }))} className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">State</label>
                      <input value={billingInfo?.state} onChange={e => setBillingInfo(p => ({ ...p, state: e?.target?.value }))} className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">ZIP Code *</label>
                      <input value={billingInfo?.postalCode} onChange={e => setBillingInfo(p => ({ ...p, postalCode: e?.target?.value }))} className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Country</label>
                      <select value={billingInfo?.country} onChange={e => setBillingInfo(p => ({ ...p, country: e?.target?.value }))} className="w-full px-3 py-2 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="GB">United Kingdom</option>
                        <option value="AU">Australia</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button onClick={() => setShowPaymentModal(false)} className="flex-1 px-4 py-3 border border-border rounded-lg text-foreground font-medium hover:bg-muted transition-smooth">Cancel</button>
                    <button
                      onClick={handleProceedToPayment}
                      disabled={creatingIntent}
                      className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {creatingIntent ? <><div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />Processing...</> : <>Continue to Payment<Icon name="ArrowRight" size={16} color="currentColor" /></>}
                    </button>
                  </div>
                </div>
              ) : clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                  <PaymentForm
                    clientSecret={clientSecret}
                    invoice={payingInvoice}
                    billingInfo={billingInfo}
                    onSuccess={handlePaymentSuccess}
                    onCancel={() => setBillingStep('billing')}
                  />
                </Elements>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerPaymentPortal;
